import { qs, el } from '../utils/dom.js';

/**
 * Tileset palette — displays tileset image and handles tile selection.
 * Supports single-tile and multi-tile stamp selection via drag.
 * Right-click on tileset selector for tileset settings.
 * Right-click on a tile for animation/collision editors.
 * Palette zoom (+/-) scales the CSS display size; scrollbars appear when needed.
 */
export class TilesetPalette {
  constructor(state, options = {}) {
    this.state = state;
    this._tilesetSettings = options.tilesetSettings || null;
    this._tileAnimationEditor = options.tileAnimationEditor || null;
    this._tileCollisionEditor = options.tileCollisionEditor || null;

    this.canvas = qs('#palette-canvas');
    this.ctx = this.canvas.getContext('2d');
    this.selectEl = qs('#tileset-select');
    this.infoEl = qs('#palette-tile-id');

    this._activeTilesetIndex = 0;
    this._selecting = false;
    this._selStart = null;
    this._selEnd = null;
    this._highlight = null; // { col, row, cols, rows }
    this._paletteZoom = 1; // CSS display zoom (1 = fit to panel, 2 = 2x, etc.)
    this._recentTiles = []; // Last 20 unique tile IDs
    this._recentEl = qs('#recent-tiles');
    this._bookmarks = JSON.parse(localStorage.getItem('tileforge_bookmarks') || '[]');
    this._bookmarksEl = null; // Created on first render

    this._bindEvents();
    this._addControls();
  }

  _bindEvents() {
    this.selectEl.addEventListener('change', () => {
      this._activeTilesetIndex = parseInt(this.selectEl.value, 10);
      this._autoFitZoom();
      this.render();
    });

    this.canvas.addEventListener('mousedown', (e) => this._onMouseDown(e));
    this.canvas.addEventListener('mousemove', (e) => this._onMouseMove(e));
    this.canvas.addEventListener('mouseup', (e) => this._onMouseUp(e));
    this.canvas.addEventListener('mouseleave', () => { this._selecting = false; });
    this.canvas.addEventListener('contextmenu', (e) => this._onContextMenu(e));

    // Scroll-wheel zoom on palette canvas
    const container = qs('#palette-container');
    if (container) {
      container.addEventListener('wheel', (e) => {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          this._adjustZoom(e.deltaY < 0 ? 1 : -1);
        }
      }, { passive: false });
    }

    this.state.onChange('tilesets', () => { this._updateSelect(); this._renderBookmarks(); });
    this.state.onChange('tilesetImages', () => { this.render(); this._renderBookmarks(); });
  }

  _addControls() {
    const parent = this.selectEl.parentElement;
    if (!parent) return;

    // Zoom out
    const zoomOut = el('button', {
      className: 'btn btn-sm palette-zoom-btn',
      title: 'Zoom out palette',
      onClick: () => this._adjustZoom(-1),
    }, '\u2212'); // minus sign

    // Zoom in
    const zoomIn = el('button', {
      className: 'btn btn-sm palette-zoom-btn',
      title: 'Zoom in palette',
      onClick: () => this._adjustZoom(1),
    }, '+');

    // Zoom reset / fit
    const zoomFit = el('button', {
      className: 'btn btn-sm palette-zoom-btn',
      title: 'Fit to panel',
      onClick: () => { this._autoFitZoom(); this.render(); },
    }, '\u25a3'); // filled square

    // Gear button (tileset settings)
    const gear = el('button', {
      className: 'btn btn-sm',
      title: 'Tileset Settings',
      style: { marginLeft: '4px', padding: '2px 6px', fontSize: '12px' },
      onClick: () => {
        if (this._tilesetSettings) {
          this._tilesetSettings.show(this._activeTilesetIndex);
        }
      },
    }, '\u2699');

    parent.appendChild(zoomOut);
    parent.appendChild(zoomFit);
    parent.appendChild(zoomIn);
    parent.appendChild(gear);
  }

  _adjustZoom(direction) {
    const steps = [0.5, 0.75, 1, 1.5, 2, 3, 4, 6, 8, 10, 12, 16];
    const currentIdx = steps.indexOf(this._paletteZoom);
    let newIdx;

    if (currentIdx === -1) {
      // Current zoom isn't a standard step — find nearest
      newIdx = steps.findIndex(s => s >= this._paletteZoom);
      if (newIdx === -1) newIdx = steps.length - 1;
      if (direction < 0) newIdx = Math.max(0, newIdx - 1);
    } else {
      newIdx = Math.max(0, Math.min(steps.length - 1, currentIdx + direction));
    }

    this._paletteZoom = steps[newIdx];
    this.render();
  }

  _autoFitZoom() {
    const ts = this._getTileset();
    if (!ts) { this._paletteZoom = 1; return; }
    const panelWidth = qs('#palette-container')?.clientWidth || 272;
    // Fit so tiles are at least ~12px CSS each, but don't exceed panel width at zoom 1
    const naturalFit = panelWidth / ts.imageWidth;
    const minTileCss = 12;
    const minZoomForReadable = minTileCss / (ts.tileWidth * naturalFit);
    this._paletteZoom = Math.max(1, Math.ceil(minZoomForReadable));
    // Cap at 16x
    if (this._paletteZoom > 16) this._paletteZoom = 16;
  }

  _updateSelect() {
    const tilesets = this.state.get('tilesets');
    this.selectEl.innerHTML = '';
    tilesets.forEach((ts, i) => {
      const opt = document.createElement('option');
      opt.value = i;
      opt.textContent = ts.name;
      this.selectEl.appendChild(opt);
    });
    if (tilesets.length > 0) {
      // Select the most recently added tileset (last in array)
      this._activeTilesetIndex = tilesets.length - 1;
      this.selectEl.value = this._activeTilesetIndex;
      this._autoFitZoom();
      this.render();
    }
  }

  _getTileset() {
    return this.state.get('tilesets')[this._activeTilesetIndex] || null;
  }

  _getTileCoords(e) {
    const ts = this._getTileset();
    if (!ts) return null;
    const rect = this.canvas.getBoundingClientRect();
    // Scale from CSS display size to canvas pixel size
    const scaleX = this.canvas.width / rect.width;
    const scaleY = this.canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    const margin = ts.margin || 0;
    const spacing = ts.spacing || 0;
    const cellW = ts.tileWidth + spacing;
    const cellH = ts.tileHeight + spacing;
    const col = Math.floor((x - margin) / cellW);
    const row = Math.floor((y - margin) / cellH);
    const cols = ts.columns || Math.floor(ts.imageWidth / cellW);
    if (col < 0 || row < 0 || col >= cols) return null;
    const totalRows = Math.ceil(ts.tileCount / cols);
    if (row >= totalRows) return null;
    return { col, row, cols };
  }

  _onMouseDown(e) {
    if (e.button !== 0) return; // Left click only for selection
    const coords = this._getTileCoords(e);
    if (!coords) return;
    this._selecting = true;
    this._selStart = coords;
    this._selEnd = coords;
    this._applySelection();
  }

  _onMouseMove(e) {
    const coords = this._getTileCoords(e);
    if (!coords) return;
    if (this._selecting) {
      this._selEnd = coords;
      this._applySelection();
    }
    // Update info
    const ts = this._getTileset();
    if (ts) {
      const tileId = coords.row * coords.cols + coords.col + ts.firstGid;
      this.infoEl.textContent = `Tile: ${tileId} (${coords.col}, ${coords.row})`;
    }
  }

  _onMouseUp(e) {
    if (!this._selecting) return;
    this._selecting = false;
    this._applySelection();
  }

  _onContextMenu(e) {
    e.preventDefault();
    const coords = this._getTileCoords(e);
    if (!coords) return;

    const ts = this._getTileset();
    if (!ts) return;
    const tileId = coords.row * coords.cols + coords.col + ts.firstGid;

    // Build context menu
    const menu = el('div', {
      className: 'menu-dropdown',
      style: {
        position: 'fixed',
        left: `${e.clientX}px`,
        top: `${e.clientY}px`,
        zIndex: '10000',
      },
    });

    const isBookmarked = this._bookmarks.includes(tileId);
    const items = [
      { label: isBookmarked ? 'Remove Bookmark' : 'Bookmark Tile', action: () => this._toggleBookmark(tileId) },
      { separator: true },
      { label: `Edit Animation (Tile ${tileId})`, action: () => this._tileAnimationEditor?.show(tileId) },
      { label: `Edit Collision (Tile ${tileId})`, action: () => this._tileCollisionEditor?.show(tileId) },
      { separator: true },
      { label: 'Tileset Settings...', action: () => this._tilesetSettings?.show(this._activeTilesetIndex) },
      { label: 'Remove Tileset', action: () => this._removeTileset(this._activeTilesetIndex) },
    ];

    for (const item of items) {
      if (item.separator) {
        menu.appendChild(el('div', { className: 'menu-separator' }));
        continue;
      }
      const menuItem = el('div', { className: 'menu-dropdown-item' });
      menuItem.appendChild(el('span', {}, item.label));
      menuItem.addEventListener('click', (ev) => {
        ev.stopPropagation();
        menu.remove();
        if (item.action) item.action();
      });
      menu.appendChild(menuItem);
    }

    document.body.appendChild(menu);

    // Close on click outside
    const closeMenu = () => {
      menu.remove();
      document.removeEventListener('click', closeMenu);
    };
    setTimeout(() => document.addEventListener('click', closeMenu), 0);
  }

  _applySelection() {
    const ts = this._getTileset();
    if (!ts || !this._selStart || !this._selEnd) return;

    const minCol = Math.min(this._selStart.col, this._selEnd.col);
    const maxCol = Math.max(this._selStart.col, this._selEnd.col);
    const minRow = Math.min(this._selStart.row, this._selEnd.row);
    const maxRow = Math.max(this._selStart.row, this._selEnd.row);
    const cols = this._selStart.cols;

    this._highlight = { col: minCol, row: minRow, cols: maxCol - minCol + 1, rows: maxRow - minRow + 1 };

    if (this._highlight.cols === 1 && this._highlight.rows === 1) {
      // Single tile
      const tileId = minRow * cols + minCol + ts.firstGid;
      this.state.set('selectedTileId', tileId);
      this.state.set('stamp', null);
      this._addRecentTile(tileId);
    } else {
      // Multi-tile stamp
      const stamp = [];
      for (let r = minRow; r <= maxRow; r++) {
        const row = [];
        for (let c = minCol; c <= maxCol; c++) {
          row.push(r * cols + c + ts.firstGid);
        }
        stamp.push(row);
      }
      this.state.set('selectedTileId', stamp[0][0]);
      this.state.set('stamp', stamp);
    }

    this.render();
  }

  _removeTileset(index) {
    const tilesets = this.state.get('tilesets');
    const ts = tilesets[index];
    if (!ts) return;

    if (!confirm(`Remove tileset "${ts.name}"?\n\nTiles from this tileset that are already placed on the map will become invalid.`)) {
      return;
    }

    // Remove the image from tilesetImages
    const images = this.state.get('tilesetImages');
    if (ts.image) images.delete(ts.image);

    // Remove from tilesets array
    tilesets.splice(index, 1);

    // Update active index
    this._activeTilesetIndex = Math.max(0, Math.min(this._activeTilesetIndex, tilesets.length - 1));

    this.state._notify('tilesets', tilesets, tilesets);
    this.state._notify('tilesetImages', images, images);
  }

  _toggleBookmark(tileId) {
    const idx = this._bookmarks.indexOf(tileId);
    if (idx >= 0) {
      this._bookmarks.splice(idx, 1);
    } else {
      this._bookmarks.push(tileId);
    }
    localStorage.setItem('tileforge_bookmarks', JSON.stringify(this._bookmarks));
    this._renderBookmarks();
  }

  _renderBookmarks() {
    // Insert bookmarks section before recent tiles
    if (!this._bookmarksEl) {
      this._bookmarksEl = el('div', { id: 'bookmark-tiles' });
      if (this._recentEl) {
        this._recentEl.before(this._bookmarksEl);
      }
    }
    this._bookmarksEl.innerHTML = '';
    if (this._bookmarks.length === 0) { this._bookmarksEl.style.display = 'none'; return; }
    this._bookmarksEl.style.display = '';

    const tilesets = this.state.get('tilesets');
    const images = this.state.get('tilesetImages');
    const size = 24;

    for (const tileId of this._bookmarks) {
      let ts = null;
      for (const t of tilesets) {
        if (tileId >= t.firstGid && tileId < t.firstGid + t.tileCount) { ts = t; break; }
      }
      if (!ts) continue;
      const img = images.get(ts.image);
      if (!img) continue;

      const localId = tileId - ts.firstGid;
      const srcCol = localId % ts.columns;
      const srcRow = Math.floor(localId / ts.columns);
      const sx = srcCol * ts.tileWidth + (ts.margin || 0) + srcCol * (ts.spacing || 0);
      const sy = srcRow * ts.tileHeight + (ts.margin || 0) + srcRow * (ts.spacing || 0);

      const canvas = el('canvas', {
        className: 'bookmark-tile',
        title: `Tile ${tileId} (bookmarked)`,
        onClick: () => {
          this.state.set('selectedTileId', tileId);
          this.state.set('stamp', null);
        },
      });
      canvas.width = size;
      canvas.height = size;
      canvas.style.imageRendering = 'pixelated';
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(img, sx, sy, ts.tileWidth, ts.tileHeight, 0, 0, size, size);
      this._bookmarksEl.appendChild(canvas);
    }
  }

  _addRecentTile(tileId) {
    this._recentTiles = this._recentTiles.filter(id => id !== tileId);
    this._recentTiles.unshift(tileId);
    if (this._recentTiles.length > 20) this._recentTiles.length = 20;
    this._renderRecentTiles();
  }

  _renderRecentTiles() {
    if (!this._recentEl) return;
    this._recentEl.innerHTML = '';
    if (this._recentTiles.length === 0) return;

    const tilesets = this.state.get('tilesets');
    const images = this.state.get('tilesetImages');
    const size = 24;

    for (const tileId of this._recentTiles) {
      // Find which tileset this tile belongs to
      const rect = this.state.getTileRect ? this.state.getTileRect(tileId) : null;
      let ts = null;
      for (const t of tilesets) {
        if (tileId >= t.firstGid && tileId < t.firstGid + t.tileCount) { ts = t; break; }
      }
      if (!ts) continue;

      const img = images.get(ts.image);
      if (!img) continue;

      const localId = tileId - ts.firstGid;
      const srcCol = localId % ts.columns;
      const srcRow = Math.floor(localId / ts.columns);
      const sx = srcCol * ts.tileWidth + (ts.margin || 0) + srcCol * (ts.spacing || 0);
      const sy = srcRow * ts.tileHeight + (ts.margin || 0) + srcRow * (ts.spacing || 0);

      const canvas = el('canvas', {
        className: 'recent-tile',
        title: `Tile ${tileId}`,
        onClick: () => {
          this.state.set('selectedTileId', tileId);
          this.state.set('stamp', null);
        },
      });
      canvas.width = size;
      canvas.height = size;
      canvas.style.imageRendering = 'pixelated';
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(img, sx, sy, ts.tileWidth, ts.tileHeight, 0, 0, size, size);
      this._recentEl.appendChild(canvas);
    }
  }

  render() {
    const ts = this._getTileset();
    if (!ts) {
      this.canvas.width = 200;
      this.canvas.height = 100;
      this.canvas.style.width = '';
      this.canvas.style.maxWidth = '100%';
      this.ctx.fillStyle = '#2a2a3e';
      this.ctx.fillRect(0, 0, 200, 100);
      this.ctx.fillStyle = '#888';
      this.ctx.font = '14px monospace';
      this.ctx.fillText('No tileset loaded', 20, 50);
      return;
    }

    const img = this.state.get('tilesetImages').get(ts.image);
    if (!img) return;

    // Canvas resolution = native tileset size (always)
    this.canvas.width = ts.imageWidth;
    this.canvas.height = ts.imageHeight;

    // CSS display size = native * paletteZoom
    // At zoom 1, max-width:100% fits to panel. At zoom >1, explicit width enables scrolling.
    const panelWidth = qs('#palette-container')?.clientWidth || 272;
    const cssWidth = ts.imageWidth * this._paletteZoom;

    if (cssWidth <= panelWidth) {
      // Fits in panel — let CSS handle it
      this.canvas.style.width = '';
      this.canvas.style.maxWidth = '100%';
    } else {
      // Wider than panel — set explicit width, scrollbar appears
      this.canvas.style.width = `${cssWidth}px`;
      this.canvas.style.maxWidth = 'none';
    }

    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.drawImage(img, 0, 0);

    // Draw animation indicators
    const tileAnimations = this.state.get('tileAnimations');
    if (tileAnimations) {
      const margin = ts.margin || 0;
      const spacing = ts.spacing || 0;
      const cols = ts.columns || Math.floor(ts.imageWidth / (ts.tileWidth + spacing));
      for (const [globalId] of tileAnimations) {
        if (globalId >= ts.firstGid && globalId < ts.firstGid + ts.tileCount) {
          const localId = globalId - ts.firstGid;
          const col = localId % cols;
          const row = Math.floor(localId / cols);
          const x = margin + col * (ts.tileWidth + spacing);
          const y = margin + row * (ts.tileHeight + spacing);
          // Small animated icon indicator
          this.ctx.fillStyle = 'rgba(0, 200, 255, 0.6)';
          this.ctx.fillRect(x + ts.tileWidth - 8, y, 8, 8);
          this.ctx.fillStyle = '#fff';
          this.ctx.font = '7px sans-serif';
          this.ctx.fillText('\u25b6', x + ts.tileWidth - 7, y + 7);
        }
      }
    }

    // Draw collision indicators
    const tileCollisions = this.state.get('tileCollisions');
    if (tileCollisions) {
      const margin = ts.margin || 0;
      const spacing = ts.spacing || 0;
      const cols = ts.columns || Math.floor(ts.imageWidth / (ts.tileWidth + spacing));
      for (const [globalId] of tileCollisions) {
        if (globalId >= ts.firstGid && globalId < ts.firstGid + ts.tileCount) {
          const localId = globalId - ts.firstGid;
          const col = localId % cols;
          const row = Math.floor(localId / cols);
          const x = margin + col * (ts.tileWidth + spacing);
          const y = margin + row * (ts.tileHeight + spacing);
          // Small collision indicator
          this.ctx.fillStyle = 'rgba(255, 60, 60, 0.6)';
          this.ctx.fillRect(x, y, 8, 8);
          this.ctx.fillStyle = '#fff';
          this.ctx.font = '7px sans-serif';
          this.ctx.fillText('C', x + 1, y + 7);
        }
      }
    }

    // Draw selection highlight
    if (this._highlight) {
      const margin = ts.margin || 0;
      const spacing = ts.spacing || 0;
      const x = margin + this._highlight.col * (ts.tileWidth + spacing);
      const y = margin + this._highlight.row * (ts.tileHeight + spacing);
      const w = this._highlight.cols * (ts.tileWidth + spacing) - spacing;
      const h = this._highlight.rows * (ts.tileHeight + spacing) - spacing;

      this.ctx.strokeStyle = '#ffff00';
      this.ctx.lineWidth = 2;
      this.ctx.strokeRect(x, y, w, h);
      this.ctx.fillStyle = 'rgba(255, 255, 0, 0.15)';
      this.ctx.fillRect(x, y, w, h);
    }
  }
}
